// Factory function to create DesignSystemContent from a kit
import { Kit } from 'dragonmouse-shared/interfaces';
import { DesignSystemContent } from './types';
import { parsePrimitives } from '../primitives/parser';
import { ComponentCategory, PrimitiveCategory, TokenCategory } from '../types';
import { parseTokens } from 'designSystem/tokens/parser';
import { parseComponents } from 'designSystem/components/parser';
import { parseStories } from '../stories/parser';

// Parses tokens, primitives, and components, and groups them by category.
export function createDesignSystemContent(versionId: string, kit: Kit): DesignSystemContent {
	// Parse tokens if available (check for non-empty string)
	let parsedTokens: DesignSystemContent['tokens'] | undefined;
	if (kit.tokens && kit.tokens.trim().length > 0) {
		const tokens = parseTokens(kit.tokens);

		// Group tokens by category
		const byCategory: Record<TokenCategory, typeof tokens> = {
			color: [],
			typography: [],
			spacing: [],
			elevation: [],
			borderRadius: [],
			opacity: [],
			motion: [],
			other: [],
		};

		const byId: Record<string, (typeof tokens)[0]> = {};

		for (const token of tokens) {
			byCategory[token.category].push(token);
			byId[token.id] = token;
		}

		parsedTokens = {
			byCategory,
			byId,
			all: tokens,
		};
	}

	// Parse primitives from primitives.css if available (check for non-empty string)
	let parsedPrimitives: DesignSystemContent['primitives'] | undefined;
	if (kit.primitives && kit.primitives.trim().length > 0) {
		const primitives = parsePrimitives(kit.primitives);

		// Group primitives by category
		const byCategory: Record<PrimitiveCategory, typeof primitives> = {
			layout: [],
			typography: [],
			card: [],
			button: [],
			form: [],
			navigation: [],
			other: [],
		};

		const byId: Record<string, (typeof primitives)[0]> = {};

		for (const primitive of primitives) {
			byCategory[primitive.category].push(primitive);
			byId[primitive.id] = primitive;
		}

		parsedPrimitives = {
			byCategory,
			byId,
			all: primitives,
		};
	}

	// Parse components from components.js if available (check for non-empty string)
	let parsedComponents: DesignSystemContent['components'] | undefined;
	if (kit.components && kit.components.trim().length > 0) {
		const components = parseComponents(kit.components);

		// Group components by category
		const byCategory: Record<ComponentCategory, typeof components> = {
			button: [],
			card: [],
			form: [],
			navigation: [],
			'data-display': [],
			other: [],
		};

		const byId: Record<string, (typeof components)[0]> = {};

		for (const component of components) {
			byCategory[component.category].push(component);
			byId[component.id] = component;
		}

		parsedComponents = {
			byCategory,
			byId,
			all: components,
		};
	}

	// Parse stories from stories file if available (check for non-empty string)
	const parsedStories =
		kit.stories && kit.stories.trim().length > 0 ? parseStories(kit.stories) : [];

	return {
		versionId,
		tokens: parsedTokens,
		primitives: parsedPrimitives,
		components: parsedComponents,
		rawCode: {
			tokens: kit.tokens && kit.tokens.trim().length > 0 ? kit.tokens : '',
			primitives: kit.primitives && kit.primitives.trim().length > 0 ? kit.primitives : '',
			components: kit.components && kit.components.trim().length > 0 ? kit.components : '',
			stories: kit.stories && kit.stories.trim().length > 0 ? kit.stories : undefined,
		},
		stories: parsedStories.length > 0 ? parsedStories : undefined,
	};
}
